import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PublicLayout } from "@/components/public/PublicLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Smartphone, MessageCircle, Loader2, RefreshCw, ShieldAlert } from "lucide-react";

export default function Verification() {
  const location = useLocation();
  const [params] = useSearchParams();
  const nav = useNavigate();

  const stateData = location.state || {};
  const prospect_id = stateData.prospect_id || params.get("pid");
  const urlToken = params.get("token");
  const [phone] = useState(stateData.phone || "");
  const [channel] = useState(stateData.channel || "sms");
  const [devCode] = useState(stateData.dev_code || "");
  const [deliveryError, setDeliveryError] = useState(stateData.delivery_error || "");

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoMsg, setAutoMsg] = useState(urlToken ? "Vérification du lien en cours..." : "");

  const channelLabel = channel === "sms" ? "SMS" : "WhatsApp";

  const succeed = (data) => {
    sessionStorage.setItem(`rt_${data.prospect_id}`, data.results_token);
    toast.success("Numéro vérifié !");
    nav(`/resultats/${data.prospect_id}`, { state: { results_token: data.results_token } });
  };

  useEffect(() => {
    if (urlToken && prospect_id) {
      api.post("/verify", { prospect_id, token: urlToken })
        .then((r) => succeed(r.data))
        .catch((e) => setAutoMsg(apiError(e.response?.data?.detail)));
    }
    // eslint-disable-next-line
  }, []);

  const submitCode = async () => {
    if (code.length !== 6) { toast.error("Saisissez le code à 6 chiffres."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/verify", { prospect_id, code });
      succeed(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const resend = async (ch) => {
    try {
      const { data } = await api.post("/verify/resend", { prospect_id, channel: ch });
      if (data.already_verified) { toast.info("Numéro déjà vérifié."); return; }
      toast.success(`Un nouveau code vous a été envoyé par ${ch === "sms" ? "SMS" : "WhatsApp"}.`);
      if (data.delivery?.error) { setDeliveryError(data.delivery.error); toast.error(data.delivery.error); }
      else setDeliveryError("");
      if (data.delivery?.dev_code) toast.info(`Code (mode test) : ${data.delivery.dev_code}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!prospect_id) {
    return (
      <PublicLayout>
        <div className="max-w-md mx-auto py-24 text-center px-4">
          <ShieldAlert className="mx-auto text-indigo-500 mb-4" size={40} />
          <h1 className="font-display text-xl font-bold text-slate-900">Session introuvable</h1>
          <p className="text-slate-600 mt-2">Veuillez relancer une simulation pour accéder à votre comparatif.</p>
          <Button className="mt-6" onClick={() => nav("/")}>Retour à l'accueil</Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-md mx-auto py-16 md:py-24 px-4">
        <Card className="p-8 text-center" data-testid="verification-card">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 ${channel === "sms" ? "bg-indigo-50" : "bg-emerald-50"}`}>
            {channel === "sms" ? <Smartphone className="text-indigo-700" size={28} /> : <MessageCircle className="text-emerald-600" size={28} />}
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Confirmez votre numéro</h1>
          <p className="text-slate-600 mt-2 text-sm">
            Nous avons envoyé un code de vérification par <strong>{channelLabel}</strong> {phone ? <>au <strong>{phone}</strong></> : "à votre numéro"}. Saisissez-le ci-dessous pour accéder à votre comparatif.
          </p>

          {deliveryError && (
            <div className="mt-5 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3" data-testid="delivery-error">
              <strong>Envoi du code momentanément indisponible.</strong> {deliveryError}
            </div>
          )}
          {devCode && (
            <div className="mt-5 text-xs bg-indigo-50 border border-indigo-200 text-indigo-800 rounded-lg p-3">
              <strong>Mode test (envoi non configuré)</strong> — code de vérification : <span className="font-mono-crm font-bold">{devCode}</span>
            </div>
          )}
          {autoMsg && <p className="text-sm text-slate-500 mt-4">{autoMsg}</p>}

          <div className="flex justify-center my-7">
            <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="phone-verification-input">
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
              </InputOTPGroup>
            </InputOTP>
          </div>

          <Button className="w-full" onClick={submitCode} disabled={loading} data-testid="verify-phone-submit">
            {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            Vérifier et voir mon comparatif
          </Button>
          <div className="flex items-center justify-center gap-4 mt-4 text-sm">
            <button onClick={() => resend("whatsapp")} className="text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid="resend-whatsapp"><MessageCircle size={14} /> Renvoyer par WhatsApp</button>
            <button onClick={() => resend("sms")} className="text-blue-700 hover:underline inline-flex items-center gap-1" data-testid="resend-sms"><RefreshCw size={14} /> Par SMS</button>
          </div>
          <p className="text-xs text-slate-400 mt-6">Code valable 30 minutes, à usage unique. La vérification ne vaut pas consentement publicitaire.</p>
        </Card>
      </div>
    </PublicLayout>
  );
}
